// src/pages/UATRequest.jsx
// User Acceptance Testing (UAT) Request — Creates a SIM-T ticket

import React, { useState } from 'react';

const toolOptions = [
    'Tenant 178 - Paragon Investigations',
    'Tenant 290 - BRI',
    'Tenant 310 - Base PI',
    'Tenant 321 - Paragon',
    'Tenant 333 - Paragon',
    'Medux + 398',
    'Other'
];

const UATRequest = ({ userAlias }) => {
    const [title, setTitle] = useState('UAT Testing for {Team Name} {Tool Name}');
    const [orgTeamName, setOrgTeamName] = useState('');
    const [description, setDescription] = useState('');
    const [ldapTeams, setLdapTeams] = useState('');
    const [tool, setTool] = useState('');
    const [impact, setImpact] = useState('');
    const [duration, setDuration] = useState('');
    const [users, setUsers] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [submitError, setSubmitError] = useState('');

    const isValid = title.trim() && tool;

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const { getAccessToken } = await import('../services/auth');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
            const token = getAccessToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const descBody = [
                `## UAT Intake`,
                ``,
                `**Requester:** ${userAlias}`,
                `**Tenant:** ${tool}`,
                ``,
                `---`,
                `**1. Team Name:** ${orgTeamName || 'N/A'}`,
                ``,
                `**2. UAT description:** ${description || 'N/A'}`,
                ``,
                `**3. LDAP/Teams that the UAT relates to:** ${ldapTeams || 'Not applicable (new permission to be created)'}`,
                ``,
                `**4. Impact of the UAT:** ${impact || 'N/A'}`,
                ``,
                `**5. Time duration of the UAT (in weeks):** ${duration || 'N/A'}`,
                ``,
                `**6. List of users participating in the UAT:** ${users || 'N/A'}`,
            ].join('\n');

            const response = await fetch(`${API_BASE_URL}/validate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    operation: 'createTicket',
                    title: `[UAT] ${orgTeamName} - ${tool}`,
                    description: descBody,
                    severity: 'SEV_5',
                    requesterAlias: userAlias
                })
            });

            const raw = await response.json();
            const result = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;

            if (response.status === 401) {
                sessionStorage.clear();
                window.location.href = '/';
                return;
            }

            if (!response.ok || result.error) {
                throw new Error(result.error || `API error: ${response.status}`);
            }

            setSubmitSuccess(result);
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitSuccess) {
        return (
            <div style={{ maxWidth: '700px', margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h2 style={{ color: '#166534', marginBottom: '8px' }}>UAT Request Submitted!</h2>
                <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>Ticket ID: <strong>{submitSuccess.ticketId}</strong></p>
                {submitSuccess.ticketUrl && (
                    <a href={submitSuccess.ticketUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-block', padding: '10px 20px', borderRadius: '8px', background: '#0073bb', color: '#fff', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                        View Ticket on SIM-T ↗
                    </a>
                )}
                <div style={{ marginTop: '20px' }}>
                    <button onClick={() => window.location.href = '/'} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
                        ← Back to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '32px 24px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '4px' }}>🧪 UAT Intake</h1>
            
            {/* Info Section */}
            <div style={{ padding: '16px', borderRadius: '10px', background: '#f5f3ff', border: '1px solid #c4b5fd', marginBottom: '24px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                <p style={{ margin: '0 0 10px' }}>Use this SIM template for UAT testing in collaboration with SWAT. If you require new access on your account, kindly visit this page for the list of SWAT managed access points - <a href="https://w.amazon.com/bin/view/SWAT_Permissions%26Security/" target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb', fontWeight: '600' }}>SWAT Permissions & Security Wiki ↗</a></p>
                <p style={{ margin: '0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>(Please use this SIM for further tracking/follow ups related to the UAT and avoid multiple SIMs/approvals)</p>
            </div>

            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '20px', fontSize: '12px', color: '#92400e' }}>
                ⏱️ <strong>Note:</strong> First response time is currently 1 business day.
            </div>

            {submitError && <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b', marginBottom: '16px' }}>❌ {submitError}</div>}

            {/* Tool / Tenant */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Tenant <span style={{ color: '#d13212' }}>*</span></label>
                <select value={tool} onChange={(e) => setTool(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
                    <option value="">— Select —</option>
                    {toolOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            {/* Team Name */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>1. Team Name: <span style={{ color: '#d13212' }}>*</span></label>
                <input type="text" value={orgTeamName} onChange={(e) => setOrgTeamName(e.target.value)} placeholder="e.g., APE Compliance EU"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* UAT Description */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>2. UAT description: <span style={{ color: '#d13212' }}>*</span></label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Describe the UAT objective and scope..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* LDAP/Teams */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>3. LDAP/Teams that the UAT relates to (Not applicable in case of a new permission to be created):</label>
                <textarea value={ldapTeams} onChange={(e) => setLdapTeams(e.target.value)} rows={2} placeholder="e.g., calypso-ape-user, calypso-apuk-user"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Impact */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>4. Impact of the UAT:</label>
                <textarea value={impact} onChange={(e) => setImpact(e.target.value)} rows={3} placeholder="Describe the expected impact..."
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Duration */}
            <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>5. Time duration of the UAT (in weeks):</label>
                <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g., 2"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
            </div>

            {/* Users */}
            <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>6. List of users participating in the UAT (comma-separated, without @amazon):</label>
                <textarea value={users} onChange={(e) => setUsers(e.target.value)} rows={2} placeholder="user1, user2, user3"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
            </div>

            {/* Submit */}
            <button onClick={handleSubmit} disabled={!isValid || isSubmitting || !orgTeamName.trim() || !description.trim()}
                style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: (isValid && !isSubmitting && orgTeamName.trim() && description.trim()) ? 'pointer' : 'not-allowed', border: 'none', background: (isValid && orgTeamName.trim() && description.trim()) ? '#7c3aed' : '#ccc', color: '#fff' }}>
                {isSubmitting ? '⏳ Submitting...' : '🚀 Submit UAT Request'}
            </button>
        </div>
    );
};

export default UATRequest;
